package com.tregz.tremenz.io.model

/** The local wall-clock hour, 0..23. Reading the clock is a platform API. */
expect fun currentLocalHour(): Int

/**
 * The hourly programming behind the Channels tab. `music_videos.json` splits each channel into a-z
 * buckets; which bucket is on air depends only on the local hour, so a channel plays something
 * different over aperitif than it does at closing time.
 */
object ModelChannel {

    const val APERO = "apero"
    const val BISTRO = "bistro"
    const val CLUB = "club"

    /** The channels the tab offers, in the order they are listed. */
    val KEYS = listOf(APERO, BISTRO, CLUB)

    /**
     * The buckets on air at [hour]. The day opens at 5am and advances every two hours until noon,
     * then hourly through the night; the 4am slot sweeps up whatever is left in u-z.
     */
    fun bucketsAt(hour: Int): List<String> = when (hour.mod(24)) {
        5, 6 -> listOf("a")
        7, 8 -> listOf("b")
        9, 10 -> listOf("c")
        11 -> listOf("d")
        12 -> listOf("e")
        13 -> listOf("f")
        14 -> listOf("g")
        15 -> listOf("h")
        16 -> listOf("i")
        17 -> listOf("j")
        18 -> listOf("k")
        19 -> listOf("l")
        20 -> listOf("m")
        21 -> listOf("n")
        22 -> listOf("o")
        23 -> listOf("p")
        0 -> listOf("q")
        1 -> listOf("r")
        2 -> listOf("s")
        3 -> listOf("t")
        4 -> listOf("u", "v", "w", "x", "y", "z")
        else -> emptyList()
    }

    /**
     * What [channel] plays at [hour], drawn from [channels]. Empty when the channel is unknown or
     * when nothing is scheduled for that hour — a state the caller is expected to show, not play.
     */
    fun songsAt(
        channels: Map<String, Map<String, List<ModelData.Song>>>,
        channel: String,
        hour: Int,
        startFromBeginning: Boolean = false
    ): List<ModelData.Song> {
        val groupsToSearch = if (channel == CLUB) {
            listOf(CLUB, "dope", "electro")
        } else {
            listOf(channel)
        }

        if (startFromBeginning) {
            // The buckets the file really has, in order, so one outside a-z is not lost.
            val buckets = groupsToSearch.flatMap { g -> channels[g]?.keys.orEmpty() }.distinct().sorted()
            return buckets.flatMap { bucket ->
                groupsToSearch.flatMap { g -> channels[g]?.get(bucket).orEmpty() }
            }
        }

        for (offset in 0 until 24) {
            val checkHour = (hour + offset).mod(24)
            val songs = bucketsAt(checkHour).flatMap { bucket ->
                groupsToSearch.flatMap { g -> channels[g]?.get(bucket).orEmpty() }
            }
            if (songs.isNotEmpty()) {
                return songs
            }
        }
        return emptyList()
    }
}

/**
 * The YouTube video id of a `watch?v=…`, `youtu.be/…`, `/embed/…` or `/shorts/…` url, or null when
 * the url carries none. Only the `v` query parameter counts, not any `v=` substring.
 */
fun youtubeVideoId(url: String): String? {
    val trimmed = url.trim()
    val withoutFragment = trimmed.substringBefore('#')
    val query = withoutFragment.substringAfter('?', "")
    val fromQuery = query.split('&')
        .firstOrNull { it.startsWith("v=") }
        ?.removePrefix("v=")
        ?.ifBlank { null }
    if (fromQuery != null) return fromQuery

    val withoutScheme = withoutFragment.substringBefore('?').substringAfter("://")
    val host = withoutScheme.substringBefore('/').lowercase()
    val segments = withoutScheme.substringAfter('/', "").split('/').filter { it.isNotBlank() }
    return when {
        host == "youtu.be" || host.endsWith(".youtu.be") -> segments.firstOrNull()
        host.endsWith("youtube.com") || host.endsWith("youtube-nocookie.com") ->
            if (segments.size >= 2 && segments[0] in setOf("embed", "shorts", "live", "v")) segments[1] else null
        else -> null
    }
}

/**
 * The release year in a `release_date` field, which the catalogue writes either as a bare year or
 * as a full ISO timestamp. 0 when it is blank, which a good part of the catalogue is.
 */
fun releaseYear(releaseDate: String): Long = releaseDate.take(4).toLongOrNull() ?: 0L
